/**
 * STEMForge Email & Notification Service
 *
 * Flow:
 *  1. Admin email is sent via EmailJS (template_qgx9cmi)
 *  2. ONLY if admin email succeeds → auto-reply is sent to the client
 */

const EMAILJS_SERVICE_ID             = 'service_4wfumme';
const EMAILJS_ADMIN_TEMPLATE_ID      = 'template_qgx9cmi';
const EMAILJS_AUTO_REPLY_TEMPLATE_ID = 'template_jsr74jb';
const EMAILJS_PUBLIC_KEY             = 'MhCYRd-kWWKaAV3ho';
const ADMIN_EMAIL                    = 'thirukumaran@stemforgetechlabs.in';

export interface AdminEmailPayload {
  subject: string;
  formType: string;
  fields: Record<string, string>;
  replyTo?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildCommonParams(f: Record<string, string>): Record<string, string> {
  return {
    school_name:             f['School Name']             || f['school_name']             || '',
    board:                   f['Board']                   || f['board']                   || '',
    student_strength:        f['Student Strength']        || f['student_strength']        || '',
    current_infrastructure:  f['Current Infrastructure']  || f['current_infrastructure']  || '',
    solutions_interested:    f['Solutions Interested']    || f['solutions_interested']    || '',
    budget_range:            f['Budget Range']            || f['budget_range']            || '',
    implementation_timeline: f['Implementation Timeline'] || f['implementation_timeline'] || '',
    principal_name:          f['Principal Name']          || f['principal_name']          || '',
    designation:             f['Designation']             || f['designation']             || '',
    phone:                   f['Phone']                   || f['phone']                   || '',
    address:                 f['Address']                 || f['address']                 || '',
    preferred_date:          f['Preferred Date']          || f['preferred_date']          || '',
    preferred_time:          f['Preferred Time']          || f['preferred_time']          || '',
    notes:                   f['Notes']                   || f['notes']                   || '',
  };
}

// ─── Step 1: Admin Email ─────────────────────────────────────────────────────

/**
 * Sends the admin notification email.
 * Returns true if EmailJS confirmed delivery, false otherwise.
 */
export async function sendAdminEmail(payload: AdminEmailPayload): Promise<boolean> {
  try {
    const f = payload.fields;

    const templateParams: Record<string, string> = {
      to_email:  ADMIN_EMAIL,
      subject:   payload.subject,
      form_type: payload.formType,
      reply_to:  payload.replyTo || ADMIN_EMAIL,

      ...buildCommonParams(f),

      // Workshop-specific (used by WorkshopEnquiryModal)
      workshop:    f['Workshop']    || '',
      name:        f['Name']        || '',
      institution: f['Institution'] || '',
      role:        f['Role']        || '',
      message:     f['Message']     || '',
    };

    const emailjs = await import('@emailjs/browser');
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_ADMIN_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    return true;
  } catch {
    return false;
  }
}

// ─── Step 2: Auto-Reply (only after admin email succeeds) ────────────────────

/**
 * Sends a confirmation auto-reply to the client (school contact).
 * Must only be called after sendAdminEmail() returns true.
 * Silently fails so it never affects the form submission result.
 */
export async function sendAutoReply(payload: AdminEmailPayload): Promise<void> {
  try {
    if (!payload.replyTo) return;

    const templateParams: Record<string, string> = {
      to_email: payload.replyTo,
      reply_to: ADMIN_EMAIL,
      ...buildCommonParams(payload.fields),
    };

    const emailjs = await import('@emailjs/browser');
    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_AUTO_REPLY_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );
  } catch {
    // Silent fail — auto-reply failure must never affect the main submission
  }
}

// ─── Combined helper ─────────────────────────────────────────────────────────

/**
 * Orchestrates the full email flow:
 *  1. Send admin email → wait for confirmation
 *  2. If admin email succeeded → send auto-reply to client
 *
 * Usage: await sendEmailsInSequence(payload);
 */
export async function sendEmailsInSequence(payload: AdminEmailPayload): Promise<void> {
  const adminEmailSent = await sendAdminEmail(payload);

  if (adminEmailSent) {
    await sendAutoReply(payload);
  }
}

// ─── Chatbot Lead Capture ────────────────────────────────────────────────────

export interface ChatbotLeadPayload {
  name: string;
  school: string;
  email: string;
  phone: string;
  interest: string;
  message: string;
}

/**
 * Submits chatbot-collected lead data:
 *  1. Posts to Readdy form endpoint for storage
 *  2. Sends admin notification email via EmailJS
 *
 * Usage: await submitChatbotLead(lead);
 */
export async function submitChatbotLead(lead: ChatbotLeadPayload): Promise<boolean> {
  try {
    // 1. Post to form storage
    const formBody = new URLSearchParams();
    formBody.append('name', lead.name);
    formBody.append('school', lead.school);
    formBody.append('email', lead.email);
    formBody.append('phone', lead.phone);
    formBody.append('interest', lead.interest);
    formBody.append('message', lead.message);
    formBody.append('source', 'Chatbot');

    await fetch('https://readdy.ai/api/form/d7b1c53urf8q17puvmdg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formBody.toString(),
    });

    // 2. Notify admin via EmailJS
    const emailPayload: AdminEmailPayload = {
      subject: `Chatbot Lead: ${lead.name} from ${lead.school}`,
      formType: 'Chatbot Enquiry',
      replyTo: lead.email || ADMIN_EMAIL,
      fields: {
        'Name': lead.name,
        'School Name': lead.school,
        'Email': lead.email,
        'Phone': lead.phone,
        'Interest / Solutions': lead.interest,
        'Message / Notes': lead.message,
        'Source': 'Chatbot Widget',
      },
    };

    return await sendAdminEmail(emailPayload);
  } catch {
    return false;
  }
}
